import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Building2, Plus, ShieldCheck, Sparkles } from "lucide-react";

export default function Claim() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await http.get("/companies", { params: { q: term, limit: 25 } });
      setResults(data || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(q), 300);
    return () => clearTimeout(timer);
  }, [q, search]);

  const startClaim = async (co) => {
    if (!user) { nav(`/login?next=/claim`); return; }
    setClaiming(co.id);
    try {
      const { data } = await http.post(`/companies/${co.id}/claim`, {
        contact_email: user.email,
        contact_name: user.name,
      });
      toast.success(
        lang === "fr"
          ? `Demande envoyée ! Vérifie ta boîte mail (${user.email}) pour confirmer.`
          : `Claim submitted! Check your email (${user.email}) to confirm.`
      );
      // Route to the vitrine so the owner can preview immediately.
      nav(`/vitrine/${co.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setClaiming(null); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="relative">
            <Building2 className="w-8 h-8 mb-4 text-[#F97316]" />
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              {t(lang, "claim_hero_title")}
            </h1>
            <p className="mt-3 text-white/85 max-w-2xl leading-relaxed">
              {t(lang, "claim_hero_sub")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Badge className="bg-white/15 text-white rounded-full backdrop-blur"><ShieldCheck className="w-3 h-3 mr-1" /> {t(lang, "claim_badge_free")}</Badge>
              <Badge className="bg-white/15 text-white rounded-full backdrop-blur"><Sparkles className="w-3 h-3 mr-1" /> {t(lang, "claim_badge_ai")}</Badge>
              <Badge className="bg-white/15 text-white rounded-full backdrop-blur"><MapPin className="w-3 h-3 mr-1" /> {t(lang, "claim_badge_map")}</Badge>
            </div>
          </div>
        </div>

        {/* Search my business */}
        <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "claim_step_1")}</p>
          <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">{t(lang, "claim_search_title")}</h2>
          <p className="text-sm text-slate-600 mt-1">{t(lang, "claim_search_hint")}</p>

          <div className="mt-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="claim-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t(lang, "claim_search_placeholder")}
              className="pl-9 h-11 rounded-full"
              autoFocus
            />
          </div>

          {searching && <p className="text-xs text-slate-500 mt-3">{t(lang, "loading")}</p>}
          {!searching && q.length >= 2 && results.length === 0 && (
            <div className="mt-4 rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-6 text-center">
              <p className="text-sm text-slate-500">{t(lang, "claim_no_result")}</p>
              <Link to="/company/new">
                <Button data-testid="claim-create-new-btn" className="mt-3 bg-[#F97316] hover:bg-[#EA580C] rounded-full">
                  <Plus className="w-4 h-4 mr-1.5" /> {t(lang, "claim_create_new")}
                </Button>
              </Link>
            </div>
          )}

          <div className="mt-4 grid gap-3">
            {results.slice(0, 10).map((c) => (
              <div key={c.id} data-testid={`claim-result-${c.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-4 hover:border-[#1E3A8A]/40 transition-colors">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    <MapPin className="w-3 h-3 inline mr-0.5" /> {c.city}, {c.country} · {c.sector}
                  </p>
                  {c.claim_status === "approved" && (
                    <Badge className="mt-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px]">
                      {t(lang, "claim_already_owned")}
                    </Badge>
                  )}
                </div>
                <Button
                  data-testid={`claim-btn-${c.id}`}
                  onClick={() => startClaim(c)}
                  disabled={claiming === c.id || c.claim_status === "approved"}
                  className="shrink-0 bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-9 px-4 text-sm"
                >
                  {claiming === c.id ? "…" : c.claim_status === "approved" ? t(lang, "claim_owned_btn") : t(lang, "claim_take_over_btn")}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Create new page */}
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/30 p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "claim_step_2")}</p>
          <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">{t(lang, "claim_new_title")}</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t(lang, "claim_new_hint")}</p>
          <Link to="/company/new">
            <Button data-testid="claim-new-cta" className="mt-4 bg-[#F97316] hover:bg-[#EA580C] rounded-full h-11 px-6">
              <Plus className="w-4 h-4 mr-1.5" /> {t(lang, "claim_new_cta")}
            </Button>
          </Link>
        </div>

        {/* How it works */}
        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6">
          <h2 className="font-display text-lg font-bold text-[#1E3A8A]">{t(lang, "claim_how_title")}</h2>
          <ol className="mt-3 space-y-2 text-sm text-slate-700">
            <li><b>1.</b> {t(lang, "claim_how_1")}</li>
            <li><b>2.</b> {t(lang, "claim_how_2")}</li>
            <li><b>3.</b> {t(lang, "claim_how_3")}</li>
            <li><b>4.</b> {t(lang, "claim_how_4")}</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}
